/**
 * ShiftService - Shift management with HttpService integration
 */

import httpService from '../../../core/services/HttpService';

/**
 * Interface for shift object structure
 */
export interface Shift {
  id: number;
  start_time: string;
  end_time: string;
  employees_needed?: number;
  work_area?: string | number;
  work_area__name?: string;
  work_area__color?: string;
  work_area_id?: number | null;
  work_area_name?: string | null;
  work_area_team_id?: number | null;
  work_area_team_name?: string | null;
  work_area_team_color?: string | null;
  is_job_shift?: boolean;
  has_conflict?: boolean;
  assigned_employees?: AssignedEmployee[];
  applications?: ShiftApplication[];
  catering_notes?: string;
  notes?: string;

  // CRM Task Context
  crm_task?: number | null;
  crm_task_id?: number | null;
  crm_task_title?: string | null;
  crm_task_status?: string | null;
  crm_task_priority?: string | null;

  // Partner Context
  partner_id?: number | null;
  partner_name?: string | null;

  // Project Context
  project_id?: number | null;
  project_name?: string | null;

  // Asset Context
  asset_assignments?: AssetAssignmentInfo[];
  assets_count?: number;

  [key: string]: any;
}

/**
 * Interface for asset assignment info in shift payload
 */
export interface AssetAssignmentInfo {
  id: number;
  asset_id: number;
  asset_name: string;
  asset_type?: string;
  asset_status?: string;
}

/**
 * Interface for assigned employee object
 */
export interface AssignedEmployee {
  id: number;
  first_name: string;
  last_name: string;
  assignment_id?: number;
  [key: string]: any;
}

/**
 * Interface for shift application object
 */
export interface ShiftApplication {
  id: number;
  employee: number;
  shift: number;
  status?: string;
  created_at?: string;
  [key: string]: any;
}

/**
 * Interface for daily shifts object
 */
export interface DayShifts {
  date: string;
  shifts?: Shift[];
  [key: string]: any;
}

/**
 * Interface for shift assignment creation
 */
export interface ShiftAssignmentCreate {
  shift: number;
  employee: number;
  auto_remove_overlap?: boolean;
}

/**
 * Interface for shift creation payload
 */
export interface ShiftCreatePayload {
  start_time: string;
  end_time: string;
  employees_needed: number;
  work_area: string | number | null;
  notes?: string;
  crm_task?: number | null;
  recurrence_rule?: {
    weekdays: number[];
    interval: number;
    until?: string;
  } | null;
  never_ends?: boolean;
}

/**
 * Interface for shift update payload
 */
export interface ShiftUpdatePayload {
  start_time: string;
  end_time: string;
  employees_needed: number;
  work_area: string | number;
  notes?: string;
  crm_task?: number | null;
}

/**
 * Interface for weekly shifts fetch options
 */
export interface WeeklyShiftsFetchOptions {
  start: string;
  days?: number;
  work_area?: string;
  include_regular_shifts?: number;
  include_assets?: number;
  include_employees?: number;
}

/**
 * Shift Service
 * Centralized service for shift-related API operations using HttpService
 */
class ShiftService {
  /**
   * Fetch weekly shifts for a given date range
   * @param options - Options including start date, days, and work area filter
   * @returns Promise that resolves to daily shifts array
   */
  static async fetchWeeklyShifts(options: WeeklyShiftsFetchOptions): Promise<DayShifts[]> {
    const { start, days = 7, work_area, include_regular_shifts, include_assets, include_employees } = options;
    let url = `/api/weekly-shifts/?start=${start}&days=${days}`;

    if (work_area) {
      url += `&work_area=${work_area}`;
    }

    if (include_regular_shifts) {
      url += `&include_regular_shifts=${include_regular_shifts}`;
    }

    if (include_assets) {
      url += `&include_assets=${include_assets}`;
    }

    if (include_employees) {
      url += `&include_employees=${include_employees}`;
    }

    const response = await httpService.get<DayShifts[]>(url);
    return response.data;
  }

  /**
   * Create a new shift
   * @param payload - Shift creation data
   * @returns Promise that resolves to the created shift
   */
  static async createShift(payload: ShiftCreatePayload): Promise<Shift> {
    const response = await httpService.post<Shift>('/api/shifts/', payload);
    return response.data;
  }

  /**
   * Update a shift with scope
   * @param shiftId - The shift ID
   * @param payload - Shift update data
   * @param scope - Update scope ('this', 'future', etc.)
   * @returns Promise that resolves to the updated shift
   */
  static async updateShift(shiftId: number, payload: Partial<ShiftUpdatePayload>, scope: string = 'this'): Promise<Shift> {
    // Hinweis: preserve_dates=true signalisiert dem Backend, bei Serienänderungen
    // nur die Uhrzeit zu übernehmen und das Tages-Datum der einzelnen Instanzen
    // zu behalten. Falls das Backend den Parameter (noch) ignoriert, ist es no-op.
    const preserveDates = scope !== 'this' ? '&preserve_dates=true' : '';
    const response = await httpService.patch<Shift>(
      `/api/shifts/${shiftId}/update-scope/?scope=${scope}${preserveDates}`,
      payload
    );
    return response.data;
  }

  /**
   * Delete a shift with scope
   * @param shiftId - The shift ID
   * @param scope - Delete scope ('this', 'future', etc.)
   * @returns Promise that resolves when deletion is complete
   */
  static async deleteShift(shiftId: number, scope: string = 'this'): Promise<{ deleted_ids: number[] }> {
    const response = await httpService.delete(`/api/shifts/${shiftId}/delete-scope/?scope=${scope}`);
    return response.data;
  }

  static async undoDelete(shiftIds: number[]): Promise<{ restored_count: number }> {
    const response = await httpService.post('/api/shifts/undo-delete/', { shift_ids: shiftIds });
    return response.data;
  }

  /**
   * Create a shift assignment
   * @param payload - Assignment creation data
   * @returns Promise that resolves to the created assignment
   */
  static async createShiftAssignment(payload: ShiftAssignmentCreate): Promise<any> {
    const response = await httpService.post('/api/shift-assignments/', payload);
    return response.data;
  }

  /**
   * Delete a shift assignment
   * @param assignmentId - The assignment ID
   * @returns Promise that resolves when deletion is complete
   */
  static async deleteShiftAssignment(assignmentId: number): Promise<void> {
    await httpService.delete(`/api/shift-assignments/${assignmentId}/`);
  }

  static async getWeather(locationId: number, start: string, days = 7): Promise<any> {
    const response = await httpService.get(`/api/admin/weather/?location_id=${locationId}&start=${start}&days=${days}`);
    return response.data;
  }

  static async getWeatherMultiLocation(locationIds: number[], start: string, days = 7): Promise<any> {
    const ids = locationIds.join(',');
    const response = await httpService.get(`/api/admin/weather/?location_ids=${ids}&start=${start}&days=${days}`);
    return response.data;
  }
}

export default ShiftService; 